<template>
	<div class="customer-person-details-container">
		<AppBottomSheet :active="person !== null" :forceWidth="bottomSheetWidth" @toggling-popup="togglingPopup" >
			<div class="person-details-container" v-if="person !== null">
				<ModalHeader
					:title="person?.name+' ('+currencyStore.readableCurrencyValue(person?.balance)+')'"
					:button-text="tabButton"
					:button-disabled="cartButtonDisabled"
					@click-button="tabButtonAction" />
				<AppTabsSwitch :items="[getString('orders'), getString('details')]" @switched="i => tabIndex = i" auto-switch />
				<div class="tabs-container mt-10">
					<div class="orders-container" v-if="tabIndex === 0">
						<div class="tabs-container">
							<AppTable
								v-if="ordersTabIndex === 0"
								ref="people-table"
								:fetch-function="fetchFunctionPersonOrders"
								:table-headings="tableHeadings"
								:filters="filters"
								:title="getString('orders')"
								:total-items="customersStore.personOrdersTotal"
								:items="ordersToShow"
								initial-sort-field="date" >
								<template #settingsIcon="{item, heading}">
									<div class="row centered">
										<div v-if="item.paid=== 1 && item.type !== 'paylater'" class="table-text">{{ getString('paid') }}</div>
										<AppTableButtonGroup v-else-if="item.type === 'paylater' && item.total > 0" :buttons="buttonsPay" :item="item" @picked-pay="addToCart(item)"/>
										<div v-else class="table-text">N/A</div>
									</div>
								</template>
								<template #before-table>
									<div class="row align-center space-between row-actions mb-10">
										<div class="left-column">
											<AppTabsSwitch :items="[getString('orders'), getString('payments')]" :active-item="ordersTabIndex" @switched="i => ordersTabIndex = i" />
										</div>
										<div class="right-column">
											<AppButton small outline :text="getString('pay_all')" @click="payAll" :disabled="!userStore.checkAnyOrderPayAllowed" />
										</div>
									</div>
								</template>
								<template #order_number="{item}">
									<div class="row centered">
										<p class="payment-intent" :class="{clickable: !!item.order_id}" @click="goToOrder(item.order_id)">{{ item.order_number }}</p>
									</div>
								</template>
							</AppTable>
							<AppTable
								v-if="ordersTabIndex === 1"
								ref="people-table"
								:fetch-function="fetchFunctionPersonPayments"
								:filters="paymentsFilters"
								:table-headings="paymentsTableHeadings"
								:title="getString('orders')"
								:total-items="customersStore.personPaymentsTotal"
								:items="paymentsToShow"
								initial-sort-field="date" >
								<template #before-table>
									<div class="row align-center space-between row-actions mb-10">
										<div class="left-column">
											<AppTabsSwitch auto-switch :items="[getString('orders'), getString('payments')]" :active-item="ordersTabIndex" @switched="i => ordersTabIndex = i" />
										</div>
										<div class="right-column"></div>
									</div>
								</template>
								<template #payment-id="{item}">
									<div class="row centered">
										<p class="payment-intent">#{{ item.payment_id }}</p>
									</div>
								</template>
							</AppTable>
						</div>
					</div>
					<div class="details-container mt-10" v-else-if="tabIndex === 1">
						<CustomersPersonEditDetails @deleted-customer="emitDeletedPersonCustomer" ref="person-details" :person-id="personId" />
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<BottomPayment is-registry hide-customer-picker no-pay-later customer-not-editable :items-split="null" :active="paymentActive" @toggling-popup="togglingPayment" @payment-done="paymentDone" :total="this.cartTotal" :paid-value="0" @deleted-customer="emitDeletedPersonCustomer" :skip-tipping="true" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useValuesStore} from "@/shared/stores";
import {useCustomersStore, useEpsonStore, useOrdersStore, useUserStore} from "@/console-new/stores";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import {formatDateTime, formatDateMMDDHHmm, formatDateYYMMDD} from "@/shared/helpers/dates";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import BottomPayment from "@/console-new/components/ops/counter/BottomPayment.vue";
import CustomersPersonEditDetails from "@/console-new/components/ops/CustomersPersonEditDetails.vue";
import API from "@/shared/helpers/API";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const epsonStore = useEpsonStore();
		const ordersStore = useOrdersStore();
		const valuesStore = useValuesStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, currencyStore, userStore, customersStore, ordersStore, valuesStore, formatDateMMDDHHmm, formatDateYYMMDD, formatDateTime, epsonStore}
	},
	name: "CustomersPersonDetails",
	components: {
		AppTableButtonGroup,
		BottomPayment, AppTextInput, AppButtonIcon, AppButton, AppTable, AppTabsSwitch, AppRadioButtonGroup, AppGoogleMapsInput, AppSelect, ModalHeader, AppBottomSheet, CustomersPersonEditDetails},
	mixins: [WindowWidth],
	emits: ['toggling-popup', 'deleted-customer'],
	props: {
		personId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			tabIndex: 0,
			ordersTabIndex: 0,
			paymentActive: false,
			cart: [],
			filters: [
				{
					type: "price",
					key: "total",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('order_number'),
					cols: 1,
					sortable: false,
					fieldName: 'order_number',
					slotName: 'order_number',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('total'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_total',
				},
				{
					text: this.getString('payment'),
					cols: 1,
					fieldName: 'settings',
					slotName: 'settingsIcon',
				},
			],
			paymentsFilters: [
				{
					type: "price",
					key: "amount",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			paymentsTableHeadings: [
				{
					text: this.getString('payment_id'),
					cols: 1,
					sortable: false,
					fieldName: 'payment_id',
					slotName: 'payment-id',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					active: true,
					sortAsc: false,
					fieldName: 'date',
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_total',
				}
			],
		}
	},
	computed: {
		paymentsToShow() {
			return this.customersStore.personPayments.map(payment => {
				return {
					...payment,
					readable_total: this.currencyStore.readableCurrencyValue(payment.amount),
					date: this.formatDateMMDDHHmm(payment.date),
				}
			})
		},
		person() {
			return this.personId ? this.customersStore.customersPeople.find(person => person?.customer_id === this.personId) : null;
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		ordersToShow() {
			return this.customersStore.personOrders.map(order => {
				return {
					...order,
					readable_total: this.currencyStore.readableCurrencyValue(order.total),
					date: this.formatDateMMDDHHmm(order.date),
				}
			})
		},
		tabButton() {
			if(this.tabIndex === 0) {
				if(this.cartTotal > 0) {
					return this.currencyStore.readableCurrencyValue(this.cartTotal);
				} else {
					return this.getString('pay');
				}
			} else {
				return this.getString('update');
			}
		},
		cartTotal() {
			return this.cart.reduce((acc, curr) => acc + curr.total, 0);
		},
		cartButtonDisabled() {
			if(!this.userStore.checkAnyOrderPayAllowed) {
				return true;
			}

			if(this.tabIndex === 0) {
				return this.cartTotal <= 0;
			} else {
				return false;
			}
		},
		buttonsPay() {
			return [
				{
					emitName: "picked-pay",
					title: this.getString('pay'),
					activeFunction: (item) => this.isOrderInCart(item),
					disabled: !this.userStore.checkAnyOrderPayAllowed
				},
			]
		},
	},
	methods: {
		goToOrder(orderId) {
			if(orderId) {
				this.ordersStore.search = "id:"+orderId;
				this.ordersStore.showCompleted = true;
				this.ordersStore.filterCounter = true;
				this.ordersStore.filterDelivery = true;
				this.ordersStore.filterTakeaway = true;
				this.ordersStore.filterRooms = true;
				this.ordersStore.refreshOrders();
				this.$router.push("/ops/orders");

				this.valuesStore.forceCloseAllBottomSheets = true;
				setTimeout(() => {
					this.valuesStore.forceCloseAllBottomSheets = false;
				}, 1000);
			}
		},
		fetchFunctionPersonOrders(filters, callback) {
			if(filters.sort && filters.sort === 'readable_total') {
				filters.sort = 'total';
			}
			return this.customersStore.getFilteredPersonOrders(this.person.customer_id, filters, callback);
		},
		fetchFunctionPersonPayments(filters, callback) {
			if(filters.sort && filters.sort === 'readable_total') {
				filters.sort = 'amount';
			}
			return this.customersStore.getFilteredPersonPayments(this.person.customer_id, filters, callback);
		},
		addToCart(order) {
			let existing = this.cart.find(o => o.order_id === order.order_id);
			if(existing) {
				this.cart.splice(this.cart.indexOf(existing), 1);
			} else {
				this.cart.push({
					order_id: order.order_id,
					total: order.total,
				});
			}
		},
		isOrderInCart(order) {
			return !!(this.cart.find(o => o.order_id === order.order_id));
		},
		tabButtonAction() {
			if(this.tabIndex === 0) {
				if(this.cartTotal > 0) {
					this.paymentActive = true;
				}
			} else {
				if(this.$refs['person-details']) {
					this.$refs['person-details'].updatePerson();
				}
			}
		},
		togglingPopup(open) {
			this.$emit('toggling-popup', open);
			this.ordersTabIndex = 0;
			if(!open) {
				this.tabIndex = 0;
			}
		},
		togglingPayment(open) {
			if(!open) {
				this.paymentActive = false;
			}
		},
		paymentDone(type, value, partialType, orderAssociatedId, itemsSplit, dutchPeople, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, callback) {

			const total = this.cartTotal;
			API.init().payCustomersOrders(this.userStore.currBusinessId, this.cart.map(o => o.order_id), type, null, total, this.person.customer_id, "person", discountOnTotal, (response) => {
				this.paymentActive = false;
				callback && callback();
				if(response && response.result && response.result === 'OK') {
					this.showNotification("payment-success");
					this.cart = [];
					this.epsonStore.customFiscalReceipt("Saldo cliente", total, type, discountOnTotal ? -discountOnTotal : 0);
					this.$emit('toggling-popup', false);
				} else {
					this.showNotification("generic-fail");
				}
			});

		},
		payAll() {

			API.init().getCustomersAllOrders(this.userStore.currBusinessId, this.person.customer_id, "person", (response) => {
				if(response && response.result && response.result === 'OK' && response.orders) {
					if(response.orders.length === 0) {
						this.showNotification("nothing-to-pay");
						return;
					}
					this.cart = response.orders.map(o => {
						return {
							order_id: Number(o.order_id),
							total: o.total ? Number(o.total) : 0,
						}
					});
					this.paymentActive = true;
				} else {
					this.showNotification("generic-fail");
				}
			});

		},
		emitDeletedPersonCustomer(personId) {
			this.$emit('deleted-customer', personId);
		},
	},
	watch: {
		personId(newValue, oldValue) {
			if(newValue !== oldValue) {
				if(newValue) {
					this.tabIndex = 0;
					this.paymentActive = false;
					this.cart = [];
				}
			}
		},
	},
}
</script>

<style scoped lang="scss">
:deep(.table-wrapper) {
	padding-top: 0;
	margin-top: 10px;
	.row-actions {
		padding: 10px 10px 0;
	}
}
.orders-container {
	.card-container {
		box-shadow: none;
		&:deep(.card-header) {
			display: none;
		}
	}
}
.table-text {
	font-size: 12px;
}

.payment-intent {
	text-align: left;
	width: 100%;
	font-size: 12px;

	&.clickable {
		cursor: pointer;
		color: var(--color-primary);
		text-decoration: underline;
	}
}
</style>